import { logger } from 'common/services/logger';
import { AppRoutes } from 'modules/router';

import type { Location, LocationDescriptorObject } from 'history';

const STORAGE_VERSION = 'v1';
const ACCOUNT_RETURN_LOCATION_KEY = `natter:account:returnLocation:${STORAGE_VERSION}`;

// The settings area itself, plus pages that lead straight back into it (the Tech check exit and
// the legacy profile redirect) - returning to any of them would bounce the user back to settings.
const EXCLUDED_PATHS: string[] = [AppRoutes.Account, AppRoutes.TechCheck, AppRoutes.MyProfile];

// Saving runs on every navigation, so a broken storage is reported once per page load, not per page.
let hasWarnedSaveFailure = false;

type StoredAccountReturnLocation = {
  sessionState: string;
  location: LocationDescriptorObject;
};

export const isAccountReturnExcluded = (pathname: string) =>
  EXCLUDED_PATHS.some((path) => pathname === path || pathname.startsWith(`${path}/`));

/**
 * Remembers the page Back on Account Settings should return to, tagged with the login session so a
 * later login never reuses it. sessionStorage keeps it per tab and across the full reloads of the
 * Keycloak password change. Best-effort: never throws; warns once when storage is unavailable.
 */
export const saveAccountReturnLocation = (
  sessionState: string,
  { pathname, search, hash, state }: Pick<Location, 'pathname' | 'search' | 'hash' | 'state'>
): void => {
  const write = (location: LocationDescriptorObject) =>
    sessionStorage.setItem(
      ACCOUNT_RETURN_LOCATION_KEY,
      JSON.stringify({ sessionState, location } satisfies StoredAccountReturnLocation)
    );

  try {
    write({ pathname, search, hash, state });
  } catch {
    // The page's router state isn't serialisable - the address alone still returns to the page.
    try {
      write({ pathname, search, hash });
    } catch (error) {
      // Storage is unavailable, so Back falls back to the home page.
      if (!hasWarnedSaveFailure) {
        hasWarnedSaveFailure = true;
        logger.warn('[Account] Could not save the Account Settings return page', { error });
      }
    }
  }
};

/** The saved page for this login session, or `undefined` when there is none. */
export const getAccountReturnLocation = (sessionState: string | undefined): LocationDescriptorObject | undefined => {
  if (!sessionState) return undefined;

  try {
    const raw = sessionStorage.getItem(ACCOUNT_RETURN_LOCATION_KEY);
    if (!raw) return undefined;

    const stored = JSON.parse(raw) as Partial<StoredAccountReturnLocation> | null;
    const pathname = stored?.location?.pathname;
    const isInAppPath = typeof pathname === 'string' && pathname.startsWith('/') && !pathname.startsWith('//');

    return stored?.sessionState === sessionState && isInAppPath ? stored.location : undefined;
  } catch {
    return undefined;
  }
};

export const clearAccountReturnLocation = (): void => {
  try {
    sessionStorage.removeItem(ACCOUNT_RETURN_LOCATION_KEY);
  } catch {
    /* storage unavailable - nothing to clear */
  }
};
